import React, { useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { HelpCircle, Plus, Trash2 } from 'lucide-react';
import { htmlToText, truncate } from './ui';

/** Text as HTML for a unit value (the structured data holds plain text). */
export const escapeHtml = (text) => (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The page's FAQ as a list: questions and answers edit the same units as the preview, and each
 * item's structured-data entry follows the visible text unless its "update structured data" box is
 * cleared. Differences that already exist are shown, never fixed silently.
 */
const NO_ITEMS = new Set();

const FaqCard = ({
  faq,
  units,
  valueOf,
  canEdit,
  dirty = false,
  syncOff = NO_ITEMS,
  onToggleSync = () => {},
  onEdit,
  onHighlight,
  onStructure = () => {},
}) => {
  const [adding, setAdding] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [openDrift, setOpenDrift] = useState(() => new Set());
  const unit = (id) => units.find((u) => u.id === id);
  const hasJsonLd = !!faq.jsonLdUnitId;
  const drifted = faq.items.filter((i) => i.questionDrift || i.answerDrift).length;
  const uncovered = hasJsonLd ? faq.items.filter((i) => i.jsonIndex < 0).length : 0;
  const structureBlocked = !canEdit || dirty;
  const blockedTitle = dirty ? 'Save or discard your edits first' : undefined;

  const summary = [
    `${faq.items.length} question${faq.items.length === 1 ? '' : 's'}`,
    !hasJsonLd && 'no structured data',
    drifted > 0 && `${drifted} differ${drifted === 1 ? 's' : ''} from the structured data`,
    uncovered > 0 && `${uncovered} not in the structured data`,
    faq.jsonOnly?.length > 0 && `${faq.jsonOnly.length} only in the structured data`,
  ]
    .filter(Boolean)
    .join(' · ');

  const toggleDrift = (index) =>
    setOpenDrift((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <div className="mse-faq">
      <p className="mse-hint mb-2">{summary}</p>
      {faq.items.map((item) => {
        const q = unit(item.questionUnitId);
        const a = unit(item.answerUnitId);
        const paired = hasJsonLd && item.jsonIndex >= 0;
        const drift = item.questionDrift || item.answerDrift;
        return (
          <div key={item.index} className={`mse-faq-item${drift ? ' drift' : ''}`}>
            <div className="mse-field-head">
              <button type="button" className="mse-link-button mse-sublabel" onClick={() => q && onHighlight(q.id)}>
                {item.index + 1}.
              </button>
              {drift && (
                <button
                  type="button"
                  className="badge bg-warning border-0 mse-faq-badge"
                  onClick={() => toggleDrift(item.index)}
                  aria-expanded={openDrift.has(item.index)}
                  title="Show what the structured data says"
                >
                  Differs from structured data
                </button>
              )}
              {hasJsonLd && !paired && <Badge bg="secondary">Not in structured data</Badge>}
              {canEdit && (
                <button
                  type="button"
                  className="mse-link-button ms-auto mse-faq-remove"
                  title={blockedTitle || 'Remove this question'}
                  aria-label={`Remove question ${item.index + 1}`}
                  disabled={structureBlocked}
                  onClick={() =>
                    onStructure({ op: 'REMOVE', index: item.index, label: truncate(htmlToText(q ? valueOf(q) : item.question), 120) })
                  }
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
            {q ? (
              <Form.Control
                size="sm"
                className="mb-1"
                value={valueOf(q)}
                readOnly={!canEdit}
                onChange={(e) => onEdit(q, e.target.value)}
                aria-label={`Question ${item.index + 1}`}
              />
            ) : (
              <div className="mse-hint">{item.question}</div>
            )}
            {a ? (
              <Form.Control
                as="textarea"
                rows={3}
                size="sm"
                className={/<[a-z]/i.test(valueOf(a)) ? 'mse-mono' : undefined}
                value={valueOf(a)}
                readOnly={!canEdit}
                onChange={(e) => onEdit(a, e.target.value)}
                aria-label={`Answer ${item.index + 1}`}
              />
            ) : (
              <div className="mse-hint">{item.answer}</div>
            )}
            {paired && canEdit && (
              <Form.Check
                type="checkbox"
                id={`faq-sync-${item.index}`}
                className="mse-faq-sync"
                label="Also update the structured data"
                checked={!syncOff.has(item.index)}
                onChange={() => onToggleSync(item.index)}
              />
            )}
            {hasJsonLd && !paired && canEdit && (
              <button
                type="button"
                className="mse-link-button mse-faq-sync"
                disabled={structureBlocked}
                title={blockedTitle}
                onClick={() => onStructure({ op: 'ADD_TO_JSONLD', index: item.index })}
              >
                Add to the structured data
              </button>
            )}
            {drift && openDrift.has(item.index) && (
              <div className="mse-faq-json">
                {item.questionDrift && (
                  <div>
                    <span className="mse-sublabel">Structured data question</span>
                    <div>{item.jsonQuestion}</div>
                    {canEdit && q && (
                      <button type="button" className="mse-link-button" onClick={() => onEdit(q, escapeHtml(item.jsonQuestion))}>
                        Use this on the page
                      </button>
                    )}
                  </div>
                )}
                {item.answerDrift && (
                  <div>
                    <span className="mse-sublabel">Structured data answer</span>
                    <div>{item.jsonAnswer}</div>
                    {canEdit && a && (
                      <button type="button" className="mse-link-button" onClick={() => onEdit(a, escapeHtml(item.jsonAnswer))}>
                        Use this on the page
                      </button>
                    )}
                    {a && <div className="mse-hint mt-1">On the page: {htmlToText(valueOf(a))}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {faq.jsonOnly?.length > 0 && (
        <p className="mse-hint">
          Only in the structured data: {faq.jsonOnly.map((t) => `"${t}"`).join(', ')}.
        </p>
      )}

      {canEdit && (
        <div className="mse-faq-actions">
          {!adding ? (
            <>
              <Button size="sm" variant="outline-secondary" disabled={structureBlocked} title={blockedTitle} onClick={() => setAdding(true)}>
                <Plus size={13} className="me-1" />
                Add question
              </Button>
              {!hasJsonLd && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={structureBlocked}
                  title={blockedTitle}
                  onClick={() => onStructure({ op: 'ADD_JSONLD' })}
                >
                  <HelpCircle size={13} className="me-1" />
                  Add structured data
                </Button>
              )}
            </>
          ) : (
            <div className="mse-faq-new">
              <Form.Control size="sm" placeholder="Question" value={question} onChange={(e) => setQuestion(e.target.value)} className="mb-1" />
              <Form.Control
                as="textarea"
                rows={3}
                size="sm"
                placeholder="Answer (plain text)"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                className="mb-1"
              />
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!question.trim() || !answer.trim()}
                  onClick={() => {
                    onStructure({ op: 'ADD', index: faq.items.length - 1, question: question.trim(), answer: answer.trim() });
                    setAdding(false);
                    setQuestion('');
                    setAnswer('');
                  }}
                >
                  Review
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setAdding(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {dirty && <span className="mse-hint">Adding or removing questions needs a saved page.</span>}
        </div>
      )}
    </div>
  );
};

export default FaqCard;
